import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';
import {
  Bookmark,
  Loader2,
  Minus,
  ArrowUp,
  Users,
  Building2,
  Plus
} from 'lucide-react';
import axios from 'axios';
import { api } from '../App';
import { useStatusCategories } from '../hooks/useStatusCategories';
import { useSavedViewState } from '../hooks/useSavedViewState';
import { useWorkspaceRecordEditing } from '../hooks/useWorkspaceRecordEditing';
import AdvancedSearchHelp from '../components/AdvancedSearchHelp';
import Button from '../components/Button';
import { ErrorIcon, RefreshIcon } from '../components/icons';
import WorkspaceSelectionModal from '../components/WorkspaceSelectionModal';
import BulkWorkspaceSelectionModal from '../components/BulkWorkspaceSelectionModal';
import VotePredictionModal from '../components/VotePredictionModal';
import StopTrackingConfirmDialog from '../components/workspace/StopTrackingConfirmDialog';
import BillTable, { ViewNotesModalWithNotes } from '../components/shared/BillTable';
import NestedBillCards from '../components/savedViews/NestedBillCards';
import BillCards from '../components/cards/BillCards';
import { Bill as BillType } from '../types/bill';
import { usePageMetadata } from '../contexts/PageMetadataContext';
import LegislationFilters from '../components/legislation/LegislationFilters';
import LegislationDeepLinkBanner from '../components/legislation/LegislationDeepLinkBanner';
import SavedViewsControl from '../components/savedViews/SavedViewsControl';
import { HelpTrigger } from '../help';
import SavedViewWizard, { type WizardWorkspaceOption } from '../components/savedViews/SavedViewWizard';
import { useOrganizations } from '../hooks/useOrganizations';
import { fetchBillCustomFieldDefs } from '../services/billCustomFieldDefs';
import type { CustomFieldDefinition } from '../types/custom-fields';
import { NoteFormModal } from '../components/notes';
import { 
  getFilteredCommittees as getFilteredCommitteesHelper, 
  formatCommitteeDisplayName as formatCommitteeDisplayNameHelper,
  findCommitteeIdFromLegacy,
  hasActiveFilters as hasActiveBillFilters,
  type BillFilters,
  type FilterOptions as BillFilterOptions,
} from '../hooks/useBillFilters';
import {
  getBillStatusLabel,
  getLobbyistStatusLabel,
} from '../utils/billStatusMapping';
import { usePersistedSelection } from '../hooks/usePersistedSelection';
import ViewToggle from '../components/ViewToggle';
import EmptyState from '../components/EmptyState';
import {
  buildLegislationSearchString,
  normalizeLegislationSearchString,
  parseLegislationSearchParams,
  DEFAULT_SEARCH_SCOPE,
  type LegislationLocationState,
  type LegislationSearchScope,
  type LegislationSortDirection,
  type LegislationSortField,
} from '../utils/legislationUrlState';

interface LegislationPageProps {}

// Use the Bill type from types/bill.ts
type Bill = BillType;


// Default chamber filter - used for initial state, clear filters, and active filter detection
const DEFAULT_CHAMBER = 'All';
// Sentinel resolved server-side; never a wall-clock year (see useSessions.ts).
import { CURRENT_SESSION } from '../hooks/useSessions';

/**
 * Initial list state must match the real address bar on hard refresh. Router `location.search`
 * can be empty on the first paint (before hydration); `window.location.search` is authoritative.
 */
function getInitialLegislationState(location: { search: string }): LegislationLocationState {
  const q =
    typeof window !== 'undefined' && window.location.search
      ? window.location.search
      : location.search || '';
  return parseLegislationSearchParams(q);
}

const LegislationPage: React.FC<LegislationPageProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const initialLegislation = getInitialLegislationState(location);

  /**
   * Latest list state for URL comparison in useLayoutEffect. Updated every render so the effect
   * never reads stale filters when only location.search is in the dependency array.
   */
  const legislationListStateRef = useRef({
    filters: initialLegislation.filters,
    searchQuery: initialLegislation.searchQuery,
    searchScope: initialLegislation.searchScope,
    sortBy: initialLegislation.sortBy,
    sortDirection: initialLegislation.sortDirection,
  });

  /** True while list state was updated locally and the sync effect has not yet updated the URL. */
  const pendingLegislationUrlSyncRef = useRef(false);
  
  // Fetch status categories from database
  const { data: statusCategoriesData } = useStatusCategories();
  const statusCategories = statusCategoriesData?.data?.categories || [];

  /*
    View-mode preference. null = the user never chose, and the default then
    depends on what is showing (QA rounds 9–10): a SAVED view defaults to
    cards; the Default View (no view applied) defaults to the list/table.
    An explicit toggle wins over both and persists.
  */
  const [viewModeChoice, setViewModeChoice] = useState<'table' | 'card' | null>(() => {
    const saved = localStorage.getItem('legislationViewMode');
    return saved === 'card' || saved === 'table' ? saved : null;
  });
  useEffect(() => {
    if (viewModeChoice) localStorage.setItem('legislationViewMode', viewModeChoice);
  }, [viewModeChoice]);
  const [searchQuery, setSearchQuery] = useState(initialLegislation.searchQuery);
  /** Debounced for API only — filters/sort fetch immediately; search waits for typing pause */
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialLegislation.searchQuery);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const [searchScope, setSearchScope] = useState<LegislationSearchScope>(
    initialLegislation.searchScope
  );
  /** Set when the last list load used RAG keyword search (for result summary copy). */
  const [ragMeta, setRagMeta] = useState<{
    total_hits: number;
    returned: number;
    exclusion?: {
      banned_bill_count: number;
      ban_buckets_truncated: boolean;
      chunks_removed: number;
    };
  } | null>(null);

  const billsListFetchAbortRef = useRef<AbortController | null>(null);
  const billsListFetchGenRef = useRef(0);
  const [showStopTrackingDialog, setShowStopTrackingDialog] = useState(false);
  const [showAdvancedSearchHelp, setShowAdvancedSearchHelp] = useState(false);
  const [stopTrackingLoading] = useState(false);
  const [filters, setFilters] = useState<BillFilters>(initialLegislation.filters);
  
  const [bills, setBills] = useState<Bill[]>([]);
  // Set when the bills request itself failed (timeout, server error, malformed
  // response). Rendered as an error panel with a retry — never as the "No
  // bills found" empty state, which asserts a fact a dead request can't know.
  // A 10s-timeout falling through to the empty state is how the 2026-08-26
  // outage stayed invisible for two days with a clean console.
  const [loadError, setLoadError] = useState<string | null>(null);
  const [urlParsingComplete, setUrlParsingComplete] = useState(false);
  /* Wizard support data (page-owned: fetched by effects below). */
  const [workspaceOptions, setWorkspaceOptions] = useState<WizardWorkspaceOption[]>([]);
  const [customFieldDefs, setCustomFieldDefs] = useState<CustomFieldDefinition[]>([]);
  const { proOrganizations } = useOrganizations();
  /*
    Views are org-owned (PRD §15): only admins of an org author them, and the
    wizard needs to know WHICH orgs those are (its Organization step). Everyone
    else gets a read-and-apply list. Server-side enforcement matches — hiding
    the buttons is presentation, not the gate.
  */
  const adminOrgOptions = useMemo(
    () =>
      proOrganizations
        .filter((org) => ['org_admin', 'system_admin'].includes(String(org.role || '').toLowerCase()))
        .map((org) => ({ id: org.id, name: org.name })),
    [proOrganizations]
  );
  const canAuthorViews = adminOrgOptions.length > 0;
  /* Owner orgs of the workspaces the wizard can offer — the catalog's scope,
     with names kept alongside to disambiguate same-named fields across orgs. */
  const [workspaceOrgIds, setWorkspaceOrgIds] = useState<string[]>([]);
  const [workspaceOrgNames, setWorkspaceOrgNames] = useState<Record<string, string>>({});
  /* Notes modal for the nested CARD rendering (the table hosts its own). */
  const [cardNotesBill, setCardNotesBill] = useState<Bill | null>(null);
  const [cardNotesWorkspaceId, setCardNotesWorkspaceId] = useState<string | undefined>(undefined);

  /*
    My Saved Views (#1144) — the lifecycle lives in useSavedViewState (apply,
    reload persistence, primary auto-apply, drift, wizard state) and the
    in-place editing bundle in useWorkspaceRecordEditing. The page owns its
    filters/search/scope and URL sync, handed in as setters.
  */
  const {
    savedViews,
    appliedView,
    applySavedView,
    detachView,
    savedViewIsModified,
    viewFetchParams,
    savedViewForTable,
    nestedViewActive,
    effectiveTrackedMode,
    setTrackedOverride,
    wizardOpen,
    setWizardOpen,
    wizardEditingView,
    openNewWizard,
    openEditWizard,
    handleWizardSaved,
    wizardTrackedBillsOnly,
  } = useSavedViewState({
    filters,
    setFilters,
    searchQuery,
    setSearchQuery,
    setSearchScope,
    customFieldDefs,
    urlParsingComplete,
    locationSearch: location.search,
    onBeforeApply: () => {
      pendingLegislationUrlSyncRef.current = true;
    },
  });

  const { recordEditing, noteTarget, setNoteTarget, noteSaving, saveNote } =
    useWorkspaceRecordEditing({ setBills, customFieldDefs });

  const viewMode: 'table' | 'card' = viewModeChoice ?? (appliedView ? 'card' : 'table');
  const setViewMode = setViewModeChoice;
  const [loading, setLoading] = useState(true);
  const [totalBillsCount, setTotalBillsCount] = useState(0); // Total bills without filters
  const [trackedBillIds, setTrackedBillIds] = useState<Set<string>>(new Set());
  
  // Apply client-side tracking filter (same logic as bookmark icon display)
  const displayedBills = React.useMemo(() => {
    if (filters.trackingStatus === 'tracking') {
      // Show only bills that are tracked (filled bookmark icon)
      return bills.filter(bill => trackedBillIds.has(bill.id));
    } else if (filters.trackingStatus === 'not_tracking') {
      // Show only bills that are NOT tracked (outline bookmark icon)
      return bills.filter(bill => !trackedBillIds.has(bill.id));
    }
    // No tracking filter - show all bills
    return bills;
  }, [bills, filters.trackingStatus, trackedBillIds]);
  const [filterOptions, setFilterOptions] = useState<BillFilterOptions>({
    subjects: [],
    sponsors: [],
    committees: []
  });
  
  // Infinite scroll state
  const [hasMore, setHasMore] = useState(true);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalFilteredCount, setTotalFilteredCount] = useState(0);
  const [showingAll, setShowingAll] = useState(false);
  const BILLS_PER_PAGE = 100;

  /** RAG keyword_search: chunk page size grows on scroll / Show all (capped server-side). */
  const RAG_INITIAL_TOP_K = 40;
  const RAG_TOP_K_STEP = 40;
  const RAG_MAX_TOP_K = 200;
  const ragTopKRef = useRef(RAG_INITIAL_TOP_K);

  // Wrapper for getFilteredCommittees using imported helper
  const getFilteredCommittees = useCallback(() => {
    return getFilteredCommitteesHelper(filterOptions.committees, filters.chamber);
  }, [filterOptions.committees, filters.chamber]);

  // Filter change handler for LegislationFilters component (after filterOptions exists for chamber↔committee checks)
  const handleFilterChange = useCallback(<K extends keyof BillFilters>(key: K, value: BillFilters[K]) => {
    pendingLegislationUrlSyncRef.current = true;
    if (key === 'chamber') {
      const nextChamber = value as BillFilters['chamber'];
      setFilters(prev => {
        const next = { ...prev, chamber: nextChamber };
        if (prev.committee_id.length === 0) return next;
        // Keep the committees that still exist in the new chamber and drop only
        // the rest, rather than clearing the whole selection.
        const allowed = getFilteredCommitteesHelper(filterOptions.committees, nextChamber);
        const allowedIds = new Set(allowed.map(c => String(c.id)));
        const keptIds = prev.committee_id.filter(id => allowedIds.has(id));
        if (keptIds.length === prev.committee_id.length) return next;
        return {
          ...next,
          committee_id: keptIds,
          committee: keptIds.length > 0 ? prev.committee : '',
        };
      });
      return;
    }
    setFilters(prev => ({ ...prev, [key]: value }));
  }, [filterOptions.committees]);

  const handleCommitteeFilterChange = useCallback((committeeIds: string[]) => {
    pendingLegislationUrlSyncRef.current = true;
    setFilters(prev => ({
      ...prev,
      committee_id: committeeIds,
      // Picking from the dropdown supersedes any legacy name-based filter.
      committee: '',
    }));
  }, []);

  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [showVotePredictionsModal, setShowVotePredictionsModal] = useState(false);
  const [selectedBill, setSelectedBill] = useState<{ id: string; billNumber: string; title?: string } | null>(null);
  
  // Bulk selection state - persisted in sessionStorage to survive navigation
  const { 
    selectedIds: selectedBillIds, 
    handleSelect: handleSelectBill, 
    clearSelection, 
    selectAll 
  } = usePersistedSelection('legislation-bill-selection');
  const [showBulkBookmarkModal, setShowBulkBookmarkModal] = useState(false);

  const [bulkActionType, setBulkActionType] = useState<'add' | 'remove'>('add');
  
  // Scroll to top state
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  
  // Sorting state (seed from URL on first paint)
  const [sortBy, setSortBy] = useState<LegislationSortField>(initialLegislation.sortBy);
  const [sortDirection, setSortDirection] = useState<LegislationSortDirection>(
    initialLegislation.sortDirection
  );

  legislationListStateRef.current = {
    filters,
    searchQuery,
    searchScope,
    sortBy,
    sortDirection,
  };

  // Set page title, description, and back URL in header
  const { setPageMetadata } = usePageMetadata();
  const returnUrl = (location.state as { returnUrl?: string })?.returnUrl;
  
  useEffect(() => {
    setPageMetadata({
      title: 'Bills',
      description: 'Search and filter bills, track legislation, and manage your filters',
      backUrl: returnUrl // Use returnUrl from navigation state if present
    });
    return () => setPageMetadata(null);
  }, [setPageMetadata, returnUrl]);

  // Legacy ?committee=... → committee_id once options load (stable URLs/API).
  useEffect(() => {
    if (filters.committee_id.length > 0 || !filters.committee || filterOptions.committees.length === 0) return;
    const id = findCommitteeIdFromLegacy(filters.committee, filterOptions.committees);
    if (id) {
      pendingLegislationUrlSyncRef.current = true;
      setFilters(prev => ({ ...prev, committee_id: [id], committee: '' }));
    }
  }, [filters.committee, filters.committee_id, filterOptions.committees]);

  // Fetch total bills count without filters (only when no filters are active)
  const fetchTotalBillsCount = async (retryCount = 0) => {
    try {
      const response = await api.get('/bills?limit=1&chamber=All'); // Just get total count
      if (response.data.success) {
        setTotalBillsCount(response.data.data.total);
      }
    } catch (error: any) {
      // Retry once on network error
      if (retryCount === 0 && error.code === 'ERR_NETWORK') {
        setTimeout(() => fetchTotalBillsCount(1), 1000);
      }
    }
  };

  // Check if any filters are currently active
  const hasActiveFilters = () =>
    hasActiveBillFilters(filters, searchQuery, searchScope);

  const clearLobbyingDeepLinkFilters = () => {
    pendingLegislationUrlSyncRef.current = true;
    setFilters((prev) => ({
      ...prev,
      lobbyist_id: '',
      lobbyist_name: '',
      lobbyist_status: '',
      client_id: '',
      client_name: '',
    }));
  };

  const clearLegislatorDeepLinkFilter = () => {
    pendingLegislationUrlSyncRef.current = true;
    setFilters((prev) => ({
      ...prev,
      legislator_id: '',
      bill_status: '',
      bipartisan: false,
      chamber: DEFAULT_CHAMBER,
    }));
    setCurrentLegislator(null);
  };

  const getClientDeepLinkSuffix = (): string | undefined => {
    const parts: string[] = [];
    if (filters.lobbyist_name) {
      parts.push(`Lobbied by ${filters.lobbyist_name}`);
    } else if (
      filters.lobbyist_status &&
      filters.lobbyist_status !== 'all'
    ) {
      parts.push(getLobbyistStatusLabel(filters.lobbyist_status));
    }
    return parts.length > 0 ? parts.join(' · ') : undefined;
  };

  const getLegislatorDeepLinkSuffix = (): string | undefined => {
    const parts: string[] = [];
    if (filters.bill_status) {
      parts.push(getBillStatusLabel(filters.bill_status as 'passed' | 'failed' | 'vetoed'));
    }
    if (filters.bipartisan) {
      parts.push('Bipartisan bills');
    }
    return parts.length > 0 ? parts.join(' · ') : undefined;
  };

  const getLobbyistOnlyDeepLinkSuffix = (): string | undefined => {
    if (filters.lobbyist_status && filters.lobbyist_status !== 'all') {
      return getLobbyistStatusLabel(filters.lobbyist_status);
    }
    return undefined;
  };

  // One message per failure mode, shared by the list and RAG paths.
  const describeLoadError = (error: any): string => {
    if (error?.code === 'ECONNABORTED' || error?.code === 'ETIMEDOUT') {
      return 'The bills request timed out. Please try again.';
    }
    return error?.response?.data?.error || 'Bills failed to load. Please try again.';
  };

  // Fetch bills with current filters, search, and sorting
  // This function is intentionally not wrapped in useCallback due to many dependencies.
  // A ref is used to maintain a stable reference for the scroll handler.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchBills = async (loadMore = false, fetchAll = false) => {
    let listFetchGen = 0;
    if (!loadMore && !fetchAll) {
      billsListFetchAbortRef.current?.abort();
      billsListFetchAbortRef.current = new AbortController();
      listFetchGen = ++billsListFetchGenRef.current;
    }

    try {
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setLoadError(null);
        setCurrentOffset(0);
        setHasMore(true);
      }

      const activeRagKeyword =
        searchScope === 'fulltext' && debouncedSearchQuery.trim().length > 0;

      if (activeRagKeyword) {
        if (fetchAll) {
          ragTopKRef.current = RAG_MAX_TOP_K;
        } else if (!loadMore) {
          ragTopKRef.current = RAG_INITIAL_TOP_K;
        } else {
          const prevTopK = ragTopKRef.current;
          const nextTopK = Math.min(prevTopK + RAG_TOP_K_STEP, RAG_MAX_TOP_K);
          if (nextTopK === prevTopK) {
            setLoadingMore(false);
            setLoading(false);
            return;
          }
          ragTopKRef.current = nextTopK;
        }

        const ragSnapshotGen = billsListFetchGenRef.current;
        const signal = billsListFetchAbortRef.current?.signal;
        const topKRequested = ragTopKRef.current;

        try {
          const ragCommitteeIds = filters.committee_id.filter((id) => /^\d+$/.test(id));
          // JSON body, so multi-select filters go as arrays rather than repeated params.
          const ragBody: Record<string, string | number | boolean | string[]> = {
            keyword: debouncedSearchQuery.trim(),
            top_k: topKRequested,
            ...(filters.chamber && { chamber: filters.chamber }),
            ...(filters.subject.length > 0 && { subject: filters.subject }),
            ...(filters.status.length > 0 && { status: filters.status }),
            ...(filters.sponsor.length > 0 && { sponsor: filters.sponsor }),
            ...(ragCommitteeIds.length > 0 && { committee_id: ragCommitteeIds }),
            ...(filters.committee &&
              filters.committee_id.length === 0 && { committee: filters.committee }),
            ...(filters.legislator_id && { legislator_id: filters.legislator_id }),
            ...(filters.bill_status && { bill_status: filters.bill_status }),
            ...(filters.bipartisan && { bipartisan: true }),
            ...(filters.lobbyist_id && { lobbyist_id: filters.lobbyist_id }),
            ...(filters.lobbyist_status &&
              filters.lobbyist_status !== 'all' && { lobbyist_status: filters.lobbyist_status }),
            ...(filters.client_id && { client_id: filters.client_id }),
            ...(filters.client_name && { client_name: filters.client_name }),
            ...(filters.includeIntroduced && { includeIntroduced: true }),
            ...(filters.includeAmended && { includeAmended: true }),
            ...(filters.startDate && { startDate: filters.startDate }),
            ...(filters.endDate && { endDate: filters.endDate }),
            ...(filters.session.length > 0 && { session: filters.session }),
            /*
              Saved-view sub-records in full-text mode (#1144 H1): without
              these, a nested view + fulltext search rendered bills with no
              child rows and no explanation. JSON body, so arrays go as-is.
            */
            ...(viewFetchParams && {
              view_workspace_ids: viewFetchParams.workspaceIds,
              view_custom_field_ids: viewFetchParams.customFieldIds,
              ...(viewFetchParams.trackedOnly && { tracked_bills_only: true }),
            }),
          };
          if (filters.startDate?.trim()) {
            ragBody.date_from = filters.startDate.trim();
          }
          if (filters.endDate?.trim()) {
            ragBody.date_to = filters.endDate.trim();
          }

          const response = await api.post('/bills/rag-keyword-search', ragBody, {
            signal,
            timeout: 120000,
          });
          if (response.data.success) {
            const data = response.data.data;
            if (ragSnapshotGen !== billsListFetchGenRef.current) {
              return;
            }
            const returned = data.rag?.returned ?? 0;
            const totalHits = data.rag?.total_hits ?? 0;
            const exclusion = data.rag?.exclusion;
            setRagMeta({
              total_hits: totalHits,
              returned,
              ...(exclusion &&
              typeof exclusion.banned_bill_count === 'number' &&
              typeof exclusion.chunks_removed === 'number'
                ? {
                    exclusion: {
                      banned_bill_count: exclusion.banned_bill_count,
                      ban_buckets_truncated: Boolean(exclusion.ban_buckets_truncated),
                      chunks_removed: exclusion.chunks_removed,
                    },
                  }
                : {}),
            });
            setBills(data.bills || []);
            setTotalFilteredCount(data.total ?? 0);
            setCurrentOffset(0);
            if (fetchAll) {
              setShowingAll(true);
              setHasMore(false);
            } else {
              setShowingAll(false);
              const mightHaveMoreChunks =
                returned === topKRequested &&
                topKRequested < RAG_MAX_TOP_K &&
                totalHits > returned;
              setHasMore(mightHaveMoreChunks);
            }
          } else {
            if (ragSnapshotGen === billsListFetchGenRef.current) {
              setRagMeta(null);
              setBills([]);
              setTotalFilteredCount(0);
              setHasMore(false);
            }
          }
        } catch (error: any) {
          if (axios.isCancel?.(error) || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
            return;
          }
          if (ragSnapshotGen !== billsListFetchGenRef.current) {
            return;
          }
          setRagMeta(null);
          setBills([]);
          setTotalFilteredCount(0);
          setHasMore(false);
          setLoadError(describeLoadError(error));
        }
        return;
      }

      const offset = loadMore ? currentOffset : 0;
      const limit = fetchAll ? totalFilteredCount : BILLS_PER_PAGE;
      
      const params = new URLSearchParams({
        sortBy: sortBy,
        sortDirection: sortDirection,
        limit: limit.toString(),
        offset: fetchAll ? '0' : offset.toString(),
        ...(debouncedSearchQuery && { search: debouncedSearchQuery }),
        ...(filters.chamber && { chamber: filters.chamber }),
        ...(filters.committee &&
          filters.committee_id.length === 0 && { committee: filters.committee }),
        ...(filters.legislator_id && { legislator_id: filters.legislator_id }),
        ...(filters.bill_status && { bill_status: filters.bill_status }),
        ...(filters.bipartisan && { bipartisan: 'true' }),
        ...(filters.hot && { hot_only: 'true' }),
        ...(filters.lobbyist_id && { lobbyist_id: filters.lobbyist_id }),
        ...(filters.lobbyist_status &&
          filters.lobbyist_status !== 'all' && { lobbyist_status: filters.lobbyist_status }),
        ...(filters.client_id && { client_id: filters.client_id }),
        ...(filters.client_name && { client_name: filters.client_name }),
        ...(filters.includeIntroduced && { includeIntroduced: 'true' }),
        ...(filters.includeAmended && { includeAmended: 'true' }),
        ...(filters.startDate && { startDate: filters.startDate }),
        ...(filters.endDate && { endDate: filters.endDate }),
        // Note: trackingStatus is filtered client-side, not sent to backend
      });

      /*
        Multi-select filters go out as repeated params (?subject=a&subject=b),
        which the object-literal form above cannot express. Not comma-joined:
        subject and committee names contain commas.
      */
      const appendMulti = (key: string, values: string[]) => {
        for (const value of values) params.append(key, value);
      };
      appendMulti('subject', filters.subject);
      appendMulti('status', filters.status);
      appendMulti('sponsor', filters.sponsor);
      appendMulti('session', filters.session);
      appendMulti('committee_id', filters.committee_id.filter((id) => /^\d+$/.test(id)));

      // Saved-view sub-records (#1144): workspaces + selected custom fields.
      if (viewFetchParams) {
        appendMulti('view_workspace_ids', viewFetchParams.workspaceIds);
        appendMulti('view_custom_field_ids', viewFetchParams.customFieldIds);
        if (viewFetchParams.trackedOnly) params.append('tracked_bills_only', 'true');
      }

      const url = `/bills?${params}`;
      const signal =
        !loadMore && !fetchAll ? billsListFetchAbortRef.current?.signal : undefined;
      const response = await api.get(url, signal ? { signal } : undefined);
      
      if (response.data.success) {
        const data = response.data.data;
        setRagMeta(null);

        if (fetchAll) {
          setBills(data.bills);
          setTotalFilteredCount(data.total);
          setShowingAll(true);
          setHasMore(false); // No more to load when showing all
        } else if (loadMore) {
          setBills(prevBills => {
            const newBills = [...prevBills, ...data.bills];
            
            // Safeguard: Don't show more bills than the total that match criteria
            if (newBills.length > data.total) {
              // Truncate to the expected total
              return newBills.slice(0, data.total);
            }
            
            return newBills;
          });
          
          // Update total count on each load more to handle dynamic filtering
          setTotalFilteredCount(data.total);
        } else {
          setBills(data.bills);
          setTotalFilteredCount(data.total);
          setShowingAll(false); // Reset showing all state on new search/filter
        }
        
        if (!fetchAll) {
          setCurrentOffset(offset + BILLS_PER_PAGE);
          setHasMore(data.hasMore);
        }
      } else if (!loadMore) {
        // A 2xx whose body is not the expected shape is a failed load, not an
        // empty result.
        setRagMeta(null);
        setBills([]);
        setTotalFilteredCount(0);
        setLoadError('The server returned an unexpected response. Please try again.');
      }
    } catch (error: any) {
      if (axios.isCancel?.(error) || error?.name === 'CanceledError' || error?.code === 'ERR_CANCELED') {
        return;
      }
      // Retry once on network error for initial loads
      if (!loadMore && !fetchAll && error.code === 'ERR_NETWORK') {
        setTimeout(() => fetchBills(false, false), 1000);
        return; // Don't set loading to false yet
      }
      
      if (!loadMore) {
        setRagMeta(null);
        setBills([]);
        setTotalFilteredCount(0);
        setLoadError(describeLoadError(error));
      }
    } finally {
      if (loadMore) {
        setLoadingMore(false);
      } else {
        const superseded =
          !fetchAll &&
          listFetchGen !== 0 &&
          listFetchGen !== billsListFetchGenRef.current;
        if (!superseded) {
          setLoading(false);
        }
      }
    }
  };

  // Handle sorting - compatible with BillTable.
  // useCallback keeps the reference stable so BillTable's handleSortChanged
  // doesn't get recreated on every render.
  const handleSort = useCallback((field: string, direction: 'asc' | 'desc') => {
    pendingLegislationUrlSyncRef.current = true;
    setSortBy(field as LegislationSortField);
    setSortDirection(direction);
  }, []);

  const handleSearchQueryChange = useCallback((value: string) => {
    pendingLegislationUrlSyncRef.current = true;
    setSearchQuery(value);
  }, []);

  const handleSearchScopeChange = useCallback((scope: LegislationSearchScope) => {
    pendingLegislationUrlSyncRef.current = true;
    setSearchScope(scope);
  }, []);

  // Bulk selection handlers
  const handleSelectAll = () => {
    if (selectedBillIds.size === displayedBills.length) {
      clearSelection();
    } else {
      selectAll(displayedBills.map(bill => bill.id.toString()));
    }
  };

  const handleBulkAdd = () => {
    if (selectedBillIds.size > 0) {
      setBulkActionType('add');
      setShowBulkBookmarkModal(true);
    }
  };

  const handleBulkRemove = () => {
    if (selectedBillIds.size > 0) {
      setBulkActionType('remove');
      setShowBulkBookmarkModal(true);
    }
  };

  const handleClearSelection = () => {
    clearSelection();
  };

  // Scroll to top functionality
  const scrollToTop = () => {
    // Scroll both window and main container to top
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
    
    // Also scroll the main container if it exists and is scrolled
    const mainContainers = document.querySelectorAll('main.overflow-y-auto');
    for (const container of mainContainers) {
      if (container.scrollHeight > container.clientHeight && container.clientHeight > 0 && container.scrollTop > 0) {
        container.scrollTo({
          top: 0,
          behavior: 'smooth'
        });
        break;
      }
    }
  };

  // Handle scroll event to show/hide scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      // Check both window scroll and main container scroll
      const windowScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      
      // Find the main scrollable container (the one with actual content)
      const mainContainers = document.querySelectorAll('main.overflow-y-auto');
      let containerScrollTop = 0;
      
      // Find the main container that actually has scrollable content
      for (const container of mainContainers) {
        if (container.scrollHeight > container.clientHeight && container.clientHeight > 0) {
          containerScrollTop = container.scrollTop;
          break;
        }
      }
      
      // Show button if either window or container is scrolled
      const totalScroll = Math.max(windowScrollTop, containerScrollTop);
      setShowScrollToTop(totalScroll > 300); // Show button after scrolling 300px
    };

    const setupScrollListeners = () => {
      // Add listeners to both window and main container
      window.addEventListener('scroll', handleScroll);
      
      // Find the main container that actually has scrollable content
      const mainContainers = document.querySelectorAll('main.overflow-y-auto');
      for (const container of mainContainers) {
        if (container.scrollHeight > container.clientHeight && container.clientHeight > 0) {
          container.addEventListener('scroll', handleScroll);
          return container;
        }
      }
      return null;
    };

    // Try to setup listeners immediately
    let mainContainer = setupScrollListeners();

    // If main container wasn't ready, try again after bills load
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    if (!mainContainer) {
      retryTimer = setTimeout(() => {
        mainContainer = setupScrollListeners();
      }, 1000);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (mainContainer) {
        mainContainer.removeEventListener('scroll', handleScroll);
      }
      if (retryTimer !== null) {
        clearTimeout(retryTimer);
      }
    };
  }, [bills]); // Re-run when bills change

  const handleBulkOperation = async (workspaceIds: string[], actionType: 'add' | 'remove') => {
    const billIds = Array.from(selectedBillIds);
    
    const promises = [];
    
    if (actionType === 'add') {
      // Add bills to workspaces
      for (const billId of billIds) {
        promises.push(
          api.post('/workspace-bill-tracking', {
            billId: parseInt(billId),
            workspaceIds: workspaceIds
          })
        );
      }
    } else {
      // Remove bills from workspaces
      for (const billId of billIds) {
        promises.push(
          api.delete('/workspace-bill-tracking', {
            data: {
              billId: parseInt(billId),
              workspaceIds: workspaceIds
            }
          })
        );
      }
    }
    
    await Promise.all(promises);
    
    // Update tracked bills UI
    fetchTrackedBills();
    
    // Clear selection
    clearSelection();
    
    const actionText = actionType === 'add' ? 'added' : 'removed';
    const preposition = actionType === 'add' ? 'to' : 'from';
    alert(`Successfully ${actionText} ${billIds.length} bill${billIds.length !== 1 ? 's' : ''} ${preposition} ${workspaceIds.length} workspace${workspaceIds.length !== 1 ? 's' : ''}.`);
  };

  // Fetch filter options with chamber and session filters
  const fetchFilterOptions = useCallback(async (chamber?: string, session?: string[]) => {
    try {
      const params = new URLSearchParams();
      if (chamber) params.append('chamber', chamber);
      // Sponsors are session-scoped; several sessions means the union of their sponsors.
      for (const year of session ?? []) params.append('session', year);
      
      const response = await api.get(`/bills/filters/options?${params.toString()}`);
      if (response.data.success) {
        setFilterOptions(response.data.data);
      }
    } catch (error) {
      // Error fetching filter options
    }
  }, []);



  
  // Store current legislator info when filtering by legislator_id
  const [currentLegislator, setCurrentLegislator] = useState<{id: string, name: string} | null>(null);

  // Fetch legislator details when filtering by legislator_id
  const fetchLegislatorDetails = async (legislatorId: string) => {
    try {
      const response = await api.get(`/legislators/${legislatorId}`);
      if (response.data.success) {
        const legislator = response.data.data;
        setCurrentLegislator({
          id: legislatorId,
          name: legislator.full_name
        });
      }
    } catch (error) {
      setCurrentLegislator(null);
    }
  };

  // Apply URL → state when location.search changes. If the URL already matches the current list state
  // (e.g. after our own replace navigation), skip setState to avoid fighting the sync effect.
  // useLayoutEffect runs before the sync useEffect so state is consistent for the first compare.
  useLayoutEffect(() => {
    const { filters: f, searchQuery: sq, searchScope: ss, sortBy: sb, sortDirection: sd } =
      legislationListStateRef.current;
    const fromState = buildLegislationSearchString(f, sq, sb, sd, ss);
    const urlMatchesState =
      normalizeLegislationSearchString(fromState) ===
      normalizeLegislationSearchString(location.search || '');

    if (urlMatchesState) {
      pendingLegislationUrlSyncRef.current = false;
      if (!urlParsingComplete) {
        const { filters: f } = legislationListStateRef.current;
        fetchFilterOptions(f.chamber, f.session);
        fetchTotalBillsCount();
        if (f.legislator_id) {
          fetchLegislatorDetails(f.legislator_id);
        } else {
          setCurrentLegislator(null);
        }
        setUrlParsingComplete(true);
      }
      return;
    }

    // First paint: apply URL → state once.
    if (!urlParsingComplete) {
      const parsed = parseLegislationSearchParams(location.search);
      setFilters(parsed.filters);
      setSearchQuery(parsed.searchQuery);
      setDebouncedSearchQuery(parsed.searchQuery);
      setSearchScope(parsed.searchScope);
      setSortBy(parsed.sortBy);
      setSortDirection(parsed.sortDirection);
      fetchFilterOptions(parsed.filters.chamber, parsed.filters.session);
      fetchTotalBillsCount();
      if (parsed.filters.legislator_id) {
        fetchLegislatorDetails(parsed.filters.legislator_id);
      } else {
        setCurrentLegislator(null);
      }
      setUrlParsingComplete(true);
      return;
    }

    // After hydrate: POP → always apply URL (Back/Forward).
    if (navigationType === 'POP') {
      pendingLegislationUrlSyncRef.current = false;
      const parsed = parseLegislationSearchParams(location.search);
      setFilters(parsed.filters);
      setSearchQuery(parsed.searchQuery);
      setDebouncedSearchQuery(parsed.searchQuery);
      setSearchScope(parsed.searchScope);
      setSortBy(parsed.sortBy);
      setSortDirection(parsed.sortDirection);
      fetchFilterOptions(parsed.filters.chamber, parsed.filters.session);
      fetchTotalBillsCount();
      if (parsed.filters.legislator_id) {
        fetchLegislatorDetails(parsed.filters.legislator_id);
      } else {
        setCurrentLegislator(null);
      }
      return;
    }

    // Local list state changed but replace navigation has not run yet (Clear, committee, typing…).
    if (pendingLegislationUrlSyncRef.current) {
      return;
    }

    // In-app navigation to a new query (PUSH) — apply URL to state. Skip REPLACE (our sync effect).
    if (navigationType === 'PUSH') {
      const parsed = parseLegislationSearchParams(location.search);
      setFilters(parsed.filters);
      setSearchQuery(parsed.searchQuery);
      setDebouncedSearchQuery(parsed.searchQuery);
      setSearchScope(parsed.searchScope);
      setSortBy(parsed.sortBy);
      setSortDirection(parsed.sortDirection);
      fetchFilterOptions(parsed.filters.chamber, parsed.filters.session);
      fetchTotalBillsCount();
      if (parsed.filters.legislator_id) {
        fetchLegislatorDetails(parsed.filters.legislator_id);
      } else {
        setCurrentLegislator(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, navigationType]);

  // Keep URL in sync with list state so Back from bill detail restores filters and sort (#558).
  // Intentionally omit location.search from deps so our own replace:navigate does not retrigger this.
  useEffect(() => {
    if (!urlParsingComplete) return;

    const nextSearch = buildLegislationSearchString(
      filters,
      searchQuery,
      sortBy,
      sortDirection,
      searchScope
    );
    if (
      normalizeLegislationSearchString(nextSearch) ===
      normalizeLegislationSearchString(location.search || '')
    ) {
      return;
    }

    navigate({ pathname: '/legislation', search: nextSearch }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- location.search read for compare only; omit from deps to avoid bounce after replace
  }, [filters, searchQuery, searchScope, sortBy, sortDirection, urlParsingComplete, navigate]);

  // Re-fetch filter options when chamber or session changes to update sponsor list
  useEffect(() => {
    if (urlParsingComplete) {
      fetchFilterOptions(filters.chamber, filters.session);
    }
  }, [filters.chamber, filters.session, urlParsingComplete, fetchFilterOptions]);

  // Drop selected sponsors that are not in the current (chamber/session-scoped) list
  useEffect(() => {
    if (filters.sponsor.length === 0 || filterOptions.sponsors.length === 0) return;
    const available = new Set(filterOptions.sponsors);
    const kept = filters.sponsor.filter(s => available.has(s));
    // Only the unavailable ones go, so switching chamber keeps the rest of the selection.
    if (kept.length !== filters.sponsor.length) {
      pendingLegislationUrlSyncRef.current = true;
      setFilters(prev => ({ ...prev, sponsor: kept }));
    }
  }, [filters.sponsor, filterOptions.sponsors]);

  // Fetch when filters/sort/debounced search change (search is debounced via debouncedSearchQuery state)
  // viewFetchParams too: applying a view with the same filters still changes
  // which workspaces' sub-records the request must return.
  useEffect(() => {
    if (!urlParsingComplete) return;
    fetchBills();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, debouncedSearchQuery, searchScope, sortBy, sortDirection, urlParsingComplete, viewFetchParams]);

  // Create stable refs to avoid stale closures
  const stateRef = useRef({ loadingMore, hasMore, loading, bills: bills.length, showingAll });
  const fetchBillsRef = useRef(fetchBills);

  // Update refs when state changes (including filters, search, and sort)
  useEffect(() => {
    stateRef.current = { loadingMore, hasMore, loading, bills: bills.length, showingAll };
    fetchBillsRef.current = fetchBills;
  }, [loadingMore, hasMore, loading, bills.length, showingAll, fetchBills, filters, debouncedSearchQuery, searchScope, sortBy, sortDirection]);

  // Stable callback for AG Grid's onScrollNearEnd (fires when user scrolls
  // near the bottom of the grid's virtual viewport).
  const handleGridScrollNearEnd = useCallback(() => {
    const { loadingMore: lm, hasMore: hm, loading: ld, showingAll: sa } = stateRef.current;
    if (lm || !hm || ld || sa) return;
    fetchBillsRef.current(true);
  }, []);

  // Stable scroll handler using refs
  const handleScrollStable = useCallback(() => {
    const { loadingMore: currentLoadingMore, hasMore: currentHasMore, loading: currentLoading, showingAll: currentShowingAll } = stateRef.current;
    
    if (currentLoadingMore || !currentHasMore || currentLoading || currentShowingAll) {
      return;
    }

    let scrollTop = 0, scrollHeight = 0, clientHeight = 0, distanceFromBottom = 0;
    let usingMainContainer = false;
    
    // Find the VISIBLE main container (same logic as setup)
    const findVisibleMainContainer = () => {
      const mainContainers = document.querySelectorAll('main.overflow-y-auto');
      
      for (const container of mainContainers) {
        const rect = container.getBoundingClientRect();
        const isVisible = rect.width > 0 && rect.height > 0;
        const computedStyle = window.getComputedStyle(container);
        const isDisplayed = computedStyle.display !== 'none';
        
        if (isVisible && isDisplayed) {
          return container as HTMLElement;
        }
      }
      return null;
    };

    const mainContainer = findVisibleMainContainer();
    
    if (mainContainer) {
      scrollTop = mainContainer.scrollTop;
      scrollHeight = mainContainer.scrollHeight;
      clientHeight = mainContainer.clientHeight;
      
      if (scrollHeight > clientHeight) {
        usingMainContainer = true;
      }
    }
    
    if (!usingMainContainer) {
      scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      scrollHeight = document.documentElement.scrollHeight;
      clientHeight = window.innerHeight;
    }

    distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    // Load more when user is 200px from bottom
    if (distanceFromBottom < 200) {
      fetchBillsRef.current(true);
    }
  }, []); // Empty deps - stable function

  // Infinite scroll setup - ONCE ONLY with proper container detection
  useEffect(() => {
    // Always attach to window (remove any existing first to prevent duplicates)
    window.removeEventListener('scroll', handleScrollStable);
    window.addEventListener('scroll', handleScrollStable, { passive: true });
    
    // Find the VISIBLE main container (desktop vs mobile)
    const attachToMainContainer = () => {
      const mainContainers = document.querySelectorAll('main.overflow-y-auto');
      
      let activeContainer: Element | null = null;
      
      mainContainers.forEach((container) => {
        const rect = container.getBoundingClientRect();
        const isVisible = rect.width > 0 && rect.height > 0;
        const computedStyle = window.getComputedStyle(container);
        const isDisplayed = computedStyle.display !== 'none';
        
        // Use the first visible and displayed container
        if (isVisible && isDisplayed && !activeContainer) {
          activeContainer = container;
        }
      });
      
      if (activeContainer) {
        // Remove any existing listener first to prevent duplicates
        (activeContainer as HTMLElement).removeEventListener('scroll', handleScrollStable);
        (activeContainer as HTMLElement).addEventListener('scroll', handleScrollStable, { passive: true });
        return activeContainer as HTMLElement;
      } else {
        return null;
      }
    };

    // Try immediately
    let attachedContainer = attachToMainContainer();
    
    // Retry after a short delay if not found
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    if (!attachedContainer) {
      retryTimer = setTimeout(() => {
        attachedContainer = attachToMainContainer();
      }, 1000);
    }

    return () => {
      // Remove window listener
      window.removeEventListener('scroll', handleScrollStable);
      
      // Remove main container listeners from ALL containers
      const currentMainContainers = document.querySelectorAll('main.overflow-y-auto');
      currentMainContainers.forEach((container) => {
        container.removeEventListener('scroll', handleScrollStable);
      });
      
      if (retryTimer) {
        clearTimeout(retryTimer);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - setup ONCE ONLY






  // Fetch user's tracked bills to show bookmark status
  const fetchTrackedBills = async (retryCount = 0) => {
    try {
      const response = await api.get('/workspace-bill-tracking/user-tracked-bills');
      if (response.data.success) {
        setTrackedBillIds(new Set(response.data.data));
      }
    } catch (error: any) {
      // Don't set any state if there's an auth error to prevent interference
      if (error.response?.status === 401) {
        return;
      }
      // Retry once on network error
      if (retryCount === 0 && error.code === 'ERR_NETWORK') {
        setTimeout(() => fetchTrackedBills(1), 1000);
      }
    }
  };

  // Separate effect for authentication-dependent features (don't block core functionality)
  useEffect(() => {
    // Delay these calls to avoid interfering with initial filter setup
    const timeoutId = setTimeout(() => {
      fetchTrackedBills();
    }, 1000); // Wait 1 second after component mounts

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Wizard support data: the user's workspaces and the org's bill custom fields.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        /*
          GET /workspaces returns a BARE array (res.json(workspacesWithLogos)),
          not the {success, data} envelope most routes use — reading .data.data
          here made the wizard's Workspaces step empty for everyone. Accept
          both shapes so an envelope migration cannot silently re-break it.
        */
        const response = await api.get('/workspaces');
        const rows = (Array.isArray(response.data)
          ? response.data
          : response.data?.data || []) as Array<{
            id: string;
            name: string;
            owner_org_id?: string;
            owner_org_name?: string;
          }>;
        if (!cancelled) {
          setWorkspaceOptions(rows.map((w) => ({ id: w.id, name: w.name })));
          // Same response carries owner_org_id, so the catalog costs no extra call.
          setWorkspaceOrgIds(
            Array.from(new Set(rows.map((w) => w.owner_org_id).filter((id): id is string => !!id)))
          );
          setWorkspaceOrgNames(
            Object.fromEntries(
              rows
                .filter((w) => w.owner_org_id && w.owner_org_name)
                .map((w) => [w.owner_org_id as string, w.owner_org_name as string])
            )
          );
        }
      } catch {
        /* wizard step just shows "no workspaces available" */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /*
    Every organization the user supports contributes its bill custom fields —
    the orgs owning workspaces they can reach, UNION their own Pro memberships.
    Someone who administers several orgs builds views that pull across all of
    them, so an org must not drop out of the catalog just because it owns no
    workspace the user can currently see. (`organizations[0]` — one
    alphabetically chosen membership — is what hid the fields entirely; see
    services/billCustomFieldDefs.ts.)

    Keyed as a sorted string so a re-fetch that returns the same orgs in a new
    array does not re-run the request.
  */
  const customFieldOrgKey = useMemo(
    () =>
      Array.from(new Set([...workspaceOrgIds, ...proOrganizations.map((o) => o.id)]))
        .sort()
        .join(','),
    [workspaceOrgIds, proOrganizations]
  );
  /* Names for the "(Org)" qualifier: workspace owners plus the user's own orgs. */
  const customFieldOrgNames = useMemo(
    () => ({
      ...Object.fromEntries(proOrganizations.map((o) => [o.id, o.name])),
      ...workspaceOrgNames,
    }),
    [proOrganizations, workspaceOrgNames]
  );
  useEffect(() => {
    if (!customFieldOrgKey) return;
    let cancelled = false;
    (async () => {
      try {
        // Full definitions, not just id/name: inline editors need field_type/config.
        const defs = await fetchBillCustomFieldDefs(customFieldOrgKey.split(','));
        if (!cancelled) {
          setCustomFieldDefs(defs);
        }
      } catch {
        /* custom columns simply absent from the catalog */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customFieldOrgKey]);

  const handleClearFilters = () => {
    pendingLegislationUrlSyncRef.current = true;
    setFilters({
      chamber: DEFAULT_CHAMBER,
      subject: [],
      status: [],
      sponsor: [],
      committee_id: [],
      committee: '',
      legislator_id: '',
      bill_status: '',
      includeIntroduced: false,
      includeAmended: false,
      startDate: '',
      endDate: '',
      bipartisan: false,
      hot: false,
      lobbyist_id: '',
      lobbyist_name: '',
      lobbyist_status: '',
      client_id: '',
      client_name: '',
      trackingStatus: '',
      session: [CURRENT_SESSION]
    });
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setSearchScope(DEFAULT_SEARCH_SCOPE);
    // Clearing filters also deselects the view — the table is back to defaults.
    detachView();
    setRagMeta(null);
    setCurrentLegislator(null);
  };

  const handleBookmarkClick = (billId: string, billNumber: string, billTitle: string) => {
    // Show workspace selection modal
    setSelectedBill({ id: billId, billNumber: billNumber, title: billTitle });
    setShowBookmarkModal(true);
  };

  const handleVotePredictionsClick = (bill: Bill) => {
    // Show vote prediction modal
    setSelectedBill({ id: bill.id, billNumber: bill.billNumber, title: bill.title });
    setShowVotePredictionsModal(true);
  };

  const handleBookmarkSuccess = (workspaceIds: string[]) => {
    // Update tracked bills state
    if (selectedBill) {
      const newTrackedBills = new Set(trackedBillIds);
      if (workspaceIds.length > 0) {
        // Bill is tracked in at least one workspace
        newTrackedBills.add(selectedBill.id);
      } else {
        // Bill is not tracked in any workspace
        newTrackedBills.delete(selectedBill.id);
      }
      setTrackedBillIds(newTrackedBills);
    }
    
    // You could add a toast notification here for better UX
  };

  const handleBookmarkModalClose = () => {
    setShowBookmarkModal(false);
    setSelectedBill(null);
  };


  const handleVotePredictionsModalClose = () => {
    setShowVotePredictionsModal(false);
    setSelectedBill(null);
  };

  const handleStopTrackingConfirm = async () => {
    // Stop tracking functionality removed for standalone page
    setShowStopTrackingDialog(false);
    setSelectedBill(null);
  };

  const handleStopTrackingCancel = () => {
    setShowStopTrackingDialog(false);
    setSelectedBill(null);
  };





  const handleShowAll = async () => {
    if (totalFilteredCount > 0) {
      await fetchBills(false, true);
    }
  };

  const handleShowPaginated = () => {
    setShowingAll(false);
    fetchBills(false, false);
  };

  /*
    The header session chip was removed 2026-08-26: it duplicated the
    Filters card's Session control (one value, two widgets read as two
    filters). The card's dropdown is now the single session scope, defaulting
    to the current session via the CURRENT_SESSION sentinel in DEFAULT_FILTERS.
  */
  return (
    <div className="space-y-3 pt-2">
      {/* Search and Filters Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Filters - Uses shared LegislationFilters component */}
        <LegislationFilters
          filters={filters}
          filterOptions={filterOptions}
          searchQuery={searchQuery}
          searchScope={searchScope}
          onSearchScopeChange={handleSearchScopeChange}
          statusCategories={statusCategories}
          onFilterChange={handleFilterChange}
          onCommitteeFilterChange={handleCommitteeFilterChange}
          onSearchChange={handleSearchQueryChange}
          onClearFilters={handleClearFilters}
          onSaveFilter={canAuthorViews ? openNewWizard : undefined}
          onShowAdvancedHelp={() => setShowAdvancedSearchHelp(true)}
          getFilteredCommittees={getFilteredCommittees}
          formatCommitteeDisplayName={formatCommitteeDisplayNameHelper}
          variant="full"
          showTrackingStatus={true}
          // Opens the WIZARD (still the only authoring path, D5) seeded with
          // the current filters — a second entry point, not a second store.
          // Hidden for non-admins: authoring is org_admin-only (PRD §15).
          showSaveButton={canAuthorViews}
          saveButtonLabel="Save View"
          saveButtonIcon={Plus}
          showAdvancedSearchHelp={true}
        />

        {/* My Saved Views — one store replacing the old My Filters panel (#1144, R1).
            flex column + min-h-0 so the card stretches to the Filters card's row
            height on lg and the LIST scrolls inside it, never growing the card. */}
        <div className="card flex flex-col min-h-0">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-text-primary dark:text-text-dark-primary flex items-center gap-1.5">
              My Saved Views
              <HelpTrigger contentKey="bill-saved-views" size="sm" />
            </h3>
            {/* Creation lives here, not in the Filters box — a view is more
                than filters. Org admins only (PRD §15). */}
            {canAuthorViews && (
              <Button
                variant="primary"
                size="sm"
                icon={Plus}
                onClick={openNewWizard}
              >
                New View
              </Button>
            )}
          </div>
          <p className="text-sm text-text-tertiary dark:text-text-dark-tertiary mb-3">
            A view saves your filters, workspaces, and table columns together.
          </p>
          <SavedViewsControl
            variant="list"
            fillHeight
            views={savedViews.views}
            activeViewId={savedViews.activeViewId}
            isModified={savedViewIsModified}
            onApply={applySavedView}
            onNew={canAuthorViews ? openNewWizard : undefined}
            onEdit={canAuthorViews ? openEditWizard : undefined}
            onMakePrimary={savedViews.makePrimary}
            onDelete={canAuthorViews ? savedViews.remove : undefined}
            /* Default View = the original table: default filters, all columns. */
            onApplyDefault={handleClearFilters}
            onMakeDefaultPrimary={savedViews.clearPrimary}
            className="w-full"
          />
        </div>
      </div>
      {/* View Toggle and Results */}
      <div className="flex justify-between items-center">
        {/* View toggle - hidden on mobile (cards only on mobile) */}
        <div className="hidden lg:flex items-center space-x-2">
          <ViewToggle value={viewMode} onChange={setViewMode} />
        </div>
        <div className="flex items-center gap-4">
          {loading && (
            <div className="flex items-center gap-2 text-text-secondary dark:text-text-dark-secondary">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Loading...</span>
            </div>
          )}
          <div className="flex items-center gap-2">
            <p className="text-sm text-text-secondary dark:text-text-dark-secondary">
              {ragMeta
                ? `${displayedBills.length.toLocaleString()} bill${
                    displayedBills.length === 1 ? '' : 's'
                  } from full bill text search • ${ragMeta.returned.toLocaleString()} index hits on this page${
                    ragMeta.exclusion
                      ? ` (${ragMeta.exclusion.chunks_removed.toLocaleString()} removed by exclusions) • ${ragMeta.total_hits.toLocaleString()} chunk matches for your terms before exclusions • ${ragMeta.exclusion.banned_bill_count.toLocaleString()} bill${
                          ragMeta.exclusion.banned_bill_count === 1 ? '' : 's'
                        } with excluded text${
                          ragMeta.exclusion.ban_buckets_truncated
                            ? ' (exclusion list may be incomplete)'
                            : ''
                        }`
                      : ` • ${ragMeta.total_hits.toLocaleString()} total text matches`
                  }`
                : hasActiveFilters()
                  ? `${totalFilteredCount.toLocaleString()} bills match criteria (showing ${displayedBills.length.toLocaleString()}) • ${totalBillsCount.toLocaleString()} total bills`
                  : `${totalFilteredCount.toLocaleString()} bills found (showing ${displayedBills.length.toLocaleString()})`}
            </p>
            {!showingAll && !ragMeta && totalFilteredCount > displayedBills.length && (
              <button
                onClick={handleShowAll}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium hover:underline transition-colors"
              >
                Show All
              </button>
            )}
            {showingAll && (
              <button
                onClick={handleShowPaginated}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium hover:underline transition-colors"
              >
                Show Paginated
              </button>
            )}
          </div>
        </div>
      </div>


      {/* Results */}
      <div>
      
      {/* Deep-link filter banners */}
      {currentLegislator && filters.legislator_id && (
        <LegislationDeepLinkBanner
          icon={Users}
          label="Filtering bills sponsored by:"
          entityName={currentLegislator.name}
          suffix={getLegislatorDeepLinkSuffix()}
          onClear={clearLegislatorDeepLinkFilter}
        />
      )}

      {filters.client_id && filters.client_name && (
        <LegislationDeepLinkBanner
          icon={Building2}
          label="Filtering bills lobbied for:"
          entityName={filters.client_name}
          suffix={getClientDeepLinkSuffix()}
          onClear={clearLobbyingDeepLinkFilters}
        />
      )}

      {filters.lobbyist_id && filters.lobbyist_name && !filters.client_id && (
        <LegislationDeepLinkBanner
          icon={Users}
          label="Filtering bills lobbied by:"
          entityName={filters.lobbyist_name}
          suffix={getLobbyistOnlyDeepLinkSuffix()}
          onClear={clearLobbyingDeepLinkFilters}
        />
      )}
      
      {loading && displayedBills.length === 0 ? (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
        </div>
      ) : !loading && loadError && displayedBills.length === 0 ? (
        /* The request failed — say so. Falling through to the empty state here
           would present a dead request as "no bills exist". */
        <EmptyState
          tone="error"
          icon={ErrorIcon}
          title="Bills failed to load"
          hint={loadError}
          action={
            <Button variant="secondary" icon={RefreshIcon} onClick={() => fetchBills()}>
              Try Again
            </Button>
          }
        />
      ) : !loading && displayedBills.length === 0 ? (
        <EmptyState hint="No bills found matching your criteria." />
      ) : viewMode === 'card' && nestedViewActive && savedViewForTable ? (
        <>
          <NestedBillCards
            bills={displayedBills}
            billColumns={savedViewForTable.columns.billColumns}
            billActions={
              savedViewForTable.columns.actions?.length
                ? {
                    keys: savedViewForTable.columns.actions,
                    onBookmark: handleBookmarkClick,
                    onVotePredictions: handleVotePredictionsClick,
                    trackedBillIds,
                  }
                : undefined
            }
            workspaceColumns={savedViewForTable.columns.workspaceColumns}
            labels={{
              customFieldNames: savedViewForTable.customFieldNames,
              customFieldDescriptions: savedViewForTable.customFieldDescriptions,
            }}
            onOpenNotes={(bill, record) => {
              setCardNotesWorkspaceId(record.workspaceId);
              setCardNotesBill(bill);
            }}
            trackedMode={effectiveTrackedMode}
            onTrackedModeChange={setTrackedOverride}
            editing={recordEditing}
          />
          {loadingMore && (
            <div className="flex justify-center items-center py-8">
              <div className="flex items-center gap-2 text-text-secondary dark:text-text-dark-secondary">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading more bills...</span>
              </div>
            </div>
          )}
        </>
      ) : viewMode === 'card' ? (
        <>
          {/* Bulk Action Toolbar for Card View */}
          {selectedBillIds.size > 0 && (
            <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg px-4 py-3 mb-6">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                  {selectedBillIds.size} bill{selectedBillIds.size !== 1 ? 's' : ''} selected
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    icon={Bookmark}
                    onClick={handleBulkAdd}
                  >
                    Add to Workspaces
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    icon={Minus}
                    onClick={handleBulkRemove}
                  >
                    Remove from Workspaces
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleClearSelection}
                  >
                    Clear Selection
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Selection Mode Indicator */}
          {selectedBillIds.size > 0 && (
            <div className="mb-4 p-3 bg-info-50 dark:bg-info-900/20 border border-info-200 dark:border-info-800 rounded-lg">
              <p className="text-sm text-info-700 dark:text-info-300">
                Selection mode active. Click cards to select/deselect bills, or use the toolbar above to perform bulk actions.
              </p>
            </div>
          )}

          {/* Select All Checkbox for Card View */}
          <div className="mb-4 flex items-center gap-3">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={displayedBills.length > 0 && selectedBillIds.size === displayedBills.length}
                onChange={handleSelectAll}
                className="w-4 h-4 text-primary-600 bg-background-tertiary dark:bg-background-dark-tertiary border-border-light dark:border-border-dark rounded focus:ring-primary-500 focus:ring-2"
              />
              <label className="ml-2 text-sm font-medium text-text-secondary dark:text-text-dark-secondary">
                Select All Bills
              </label>
            </div>
            {selectedBillIds.size > 0 && (
              <span className="text-sm text-text-tertiary dark:text-text-dark-tertiary">
                ({selectedBillIds.size} of {displayedBills.length} selected)
              </span>
            )}
          </div>

          <BillCards
            bills={displayedBills}
            context="legislation"
            trackedBillIds={trackedBillIds}
            selectedBills={selectedBillIds}
            showCheckboxes={true}
            onBillSelection={handleSelectBill}
            onBookmark={handleBookmarkClick}
            onVotePredictions={handleVotePredictionsClick}
          />
          
          {/* Loading More Indicator for Card View */}
          {loadingMore && (
            <div className="flex justify-center items-center py-8">
              <div className="flex items-center gap-2 text-text-secondary dark:text-text-dark-secondary">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading more bills...</span>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="card p-0 overflow-hidden relative">
          {/* Loading overlay — keeps BillTable (AG Grid) mounted so sort state is preserved */}
          {loading && (
            <div className="absolute inset-0 bg-white/60 dark:bg-background-dark-secondary/60 z-10 flex items-center justify-center rounded-lg">
              <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
            </div>
          )}
          {/* Bulk Action Toolbar */}
          {selectedBillIds.size > 0 && (
            <div className="bg-primary-50 dark:bg-primary-900/20 border-b border-primary-200 dark:border-primary-800 px-4 py-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                  {selectedBillIds.size} bill{selectedBillIds.size !== 1 ? 's' : ''} selected
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    size="sm"
                    icon={Bookmark}
                    onClick={handleBulkAdd}
                  >
                    Add to Workspaces
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    icon={Minus}
                    onClick={handleBulkRemove}
                  >
                    Remove from Workspaces
                  </Button>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={handleClearSelection}
                  >
                    Clear Selection
                  </Button>
                </div>
              </div>
            </div>
          )}
          
          {/* Mobile: Card View (< 768px) */}
          <div className="md:hidden">
            <BillCards
              bills={displayedBills}
              context="legislation"
              trackedBillIds={trackedBillIds}
              onBookmark={handleBookmarkClick}
              onVotePredictions={handleVotePredictionsClick}
            />
          </div>

          {/* Desktop: Table View (>= 768px) */}
          <div className="hidden md:block">
            <BillTable
              bills={displayedBills}
              context="legislation"
              selectedBills={selectedBillIds}
              sortBy={sortBy}
              sortDirection={sortDirection}
              trackedBillIds={trackedBillIds}
              onBillSelection={handleSelectBill}
              onSort={handleSort}
              onBookmark={handleBookmarkClick}
              onVotePredictions={handleVotePredictionsClick}
              onScrollNearEnd={handleGridScrollNearEnd}
              savedView={savedViewForTable}
              trackedMode={effectiveTrackedMode}
              onTrackedModeChange={setTrackedOverride}
              recordEditing={recordEditing}
            />
          </div>
          
          {/* Loading More Indicator */}
          {loadingMore && (
            <div className="flex justify-center items-center py-8 border-t border-border-light dark:border-border-dark">
              <div className="flex items-center gap-2 text-text-secondary dark:text-text-dark-secondary">
                <Loader2 className="w-5 h-5 animate-spin" />
                <span className="text-sm">Loading more bills...</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pagination */}
      {/* Removed pagination as sorting handles pagination */}

      {/* Notes modal for the nested card rendering */}
      {cardNotesBill && (
        <ViewNotesModalWithNotes
          bill={cardNotesBill}
          workspaceId={cardNotesWorkspaceId}
          onClose={() => {
            setCardNotesBill(null);
            setCardNotesWorkspaceId(undefined);
          }}
        />
      )}

      {/* In-place add-note for a nested workspace record */}
      {noteTarget && (
        <NoteFormModal
          isOpen={true}
          onClose={() => setNoteTarget(null)}
          workspaceId={noteTarget.record.workspaceId}
          isSaving={noteSaving}
          defaultEntities={[
            {
              entity_type: 'bill',
              entity_id: noteTarget.bill.id,
              entity_display_name: `${noteTarget.bill.billNumber} - ${noteTarget.bill.title}`,
            },
          ]}
          onSave={saveNote}
        />
      )}

      {/* My Saved Views wizard — the only authoring path (D5) */}
      <SavedViewWizard
        isOpen={wizardOpen}
        onClose={() => setWizardOpen(false)}
        onSaved={handleWizardSaved}
        existingView={wizardEditingView}
        trackedBillsOnly={wizardTrackedBillsOnly}
        seedFilters={filters}
        seedSearchQuery={searchQuery}
        seedFullTextSearch={searchScope === 'fulltext'}
        workspaceOptions={workspaceOptions}
        adminOrgOptions={adminOrgOptions}
        customFieldDefs={customFieldDefs}
        customFieldOrgNames={customFieldOrgNames}
        filterFieldProps={{
          filterOptions,
          statusCategories,
          getFilteredCommittees,
          formatCommitteeDisplayName: formatCommitteeDisplayNameHelper,
        }}
      />

      </div>

      {/* Workspace Selection Modal */}
      <WorkspaceSelectionModal
        isOpen={showBookmarkModal}
        onClose={handleBookmarkModalClose}
        entityId={selectedBill?.id || ''}
        entityLabel={selectedBill?.billNumber || ''}
        onSave={handleBookmarkSuccess}
      />

      {/* Vote Predictions Modal */}
      <VotePredictionModal
        isOpen={showVotePredictionsModal}
        onClose={handleVotePredictionsModalClose}
        billId={selectedBill?.id || ''}
        billNumber={selectedBill?.billNumber || ''}
        billTitle={selectedBill?.title || ''}
      />

      {/* Bulk Workspace Selection Modal */}
      <BulkWorkspaceSelectionModal
        isOpen={showBulkBookmarkModal}
        onClose={() => setShowBulkBookmarkModal(false)}
        entityType="bill"
        entityCount={selectedBillIds.size}
        actionType={bulkActionType}
        onSave={(workspaceIds) => handleBulkOperation(workspaceIds, bulkActionType)}
      />

      {/* Scroll to Top Button - Mobile: Above bottom nav, Desktop: Bottom right */}
      {/* Stop Tracking Confirmation Dialog */}
      <StopTrackingConfirmDialog
        isOpen={showStopTrackingDialog}
        onClose={handleStopTrackingCancel}
        onConfirm={handleStopTrackingConfirm}
        billNumber={selectedBill?.billNumber || ''}
        workspaceName={'Workspace'}
        isLoading={stopTrackingLoading}
      />

      {showScrollToTop && (
        <>
          {/* Mobile: Above bottom navigation */}
          <button
            onClick={scrollToTop}
            className="lg:hidden fixed bottom-20 left-1/2 transform -translate-x-1/2 z-50 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="Go to top"
          >
            <ArrowUp className="w-4 h-4" />
          </button>
          
          {/* Desktop: Bottom right corner */}
          <button
            onClick={scrollToTop}
            className="hidden lg:block fixed bottom-6 right-6 z-50 p-3 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
            title="Go to top"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        </>
      )}
      
      {/* Advanced Search Help Modal */}
      <AdvancedSearchHelp 
        isOpen={showAdvancedSearchHelp} 
        onClose={() => setShowAdvancedSearchHelp(false)} 
      />
    </div>
  );
};

export default LegislationPage;